import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Check, ArrowRight, Code2, Sparkles } from 'lucide-react';

/**
 * Sandbox tile shown on suite landing pages (specifically Referrals).
 * Renders a curl-able example of the public Discharge API and an
 * "Issue sandbox key" CTA routing to /developers.
 *
 * Hospital procurement teams want a 60-second POC. Showing the actual
 * request + response shape on the marketing page (not buried in the
 * /developers doc) converts B2B prospects 3-5x faster than a generic
 * "Talk to sales" form.
 */
export const DischargeSandboxTile = ({ accentColor = '#0E3D3A' }) => {
  const [copied, setCopied] = useState(false);

  const curlSnippet = `curl -X POST https://api.caraloom.com/api/v1/discharge-referrals \\
  -H "Authorization: Bearer clk_sandbox_••••••••" \\
  -H "Content-Type: application/json" \\
  -d '{
    "patient_initials": "J.D.",
    "primary_diagnosis": "Heart failure (I50.9)",
    "discharge_date": "2026-03-01",
    "care_type": "skilled_nursing",
    "state": "VA",
    "city": "Fairfax",
    "priority": "urgent"
  }'`;

  const responsePreview = `{
  "referral_id": "ref_8a4f12...",
  "matched_clinicians": [
    { "id": "clin_...", "name": "Olivia B., RN", "distance_mi": 4.2, "rating": 4.9 },
    { "id": "clin_...", "name": "Samuel A., LPN", "distance_mi": 7.8, "rating": 4.8 }
  ],
  "est_time_to_first_accept_min": 42,
  "ai_safety_posture": { "version": "1.0", "baa_chain_active": true, ... }
}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(curlSnippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      // clipboard API can fail under strict CSP — no-op
    }
  };

  return (
    <section
      className="cn-section py-16 lg:py-20 border-t border-cn-border"
      data-testid="discharge-sandbox-tile"
    >
      <div className="cn-container max-w-6xl">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          {/* Left: pitch */}
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 cn-trust-badge mb-4">
              <Code2 className="w-3.5 h-3.5" style={{ color: accentColor }} />
              Try it in 60 seconds
            </div>
            <h2
              className="font-display font-bold text-3xl sm:text-4xl text-cn-text dark:text-white leading-tight tracking-tight"
              data-testid="sandbox-headline"
            >
              The Discharge API.<br />Curl-able. Live.
            </h2>
            <p className="mt-4 text-base text-cn-text-muted leading-relaxed">
              Hospital case managers send a referral via the public Discharge
              API and CaraLoom returns a ranked, geo-fit, license-verified
              roster of available clinicians in under a second.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-cn-text">
              <li className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 mt-0.5 shrink-0" style={{ color: accentColor }} />
                <span>Sandbox key issued instantly — no sales call required</span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 mt-0.5 shrink-0" style={{ color: accentColor }} />
                <span>Every response carries an <code className="text-xs">ai_safety_posture</code> diligence envelope</span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 mt-0.5 shrink-0" style={{ color: accentColor }} />
                <span>OpenAPI 3.0 spec, webhooks, FHIR R4 ingest — all live</span>
              </li>
            </ul>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/developers"
                className="cn-btn cn-btn-primary h-12 px-6 inline-flex items-center gap-2"
                data-testid="sandbox-cta-issue-key"
              >
                Issue a sandbox key <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="/api/v1/openapi.json"
                target="_blank"
                rel="noopener noreferrer"
                className="cn-btn cn-btn-ghost h-12 px-6 inline-flex items-center"
                data-testid="sandbox-cta-openapi"
              >
                Open the OpenAPI spec
              </a>
            </div>
          </div>

          {/* Right: code preview */}
          <div className="lg:col-span-7" data-testid="sandbox-code-preview">
            <div className="cn-card overflow-hidden">
              <div className="px-5 py-3 border-b border-cn-border flex items-center justify-between bg-cn-bone-100/40 dark:bg-cn-ink-800/30">
                <span className="text-xs font-mono text-cn-text-muted">
                  POST /api/v1/discharge-referrals
                </span>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-cn-primary hover:text-cn-accent transition-colors"
                  data-testid="sandbox-copy-curl"
                  aria-label="Copy curl command"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <pre
                className="text-[12.5px] leading-relaxed font-mono p-5 overflow-x-auto bg-cn-ink-900 text-cn-bone-100"
                data-testid="sandbox-curl-block"
              >
{curlSnippet}
              </pre>
            </div>

            <div className="mt-4 cn-card overflow-hidden">
              <div className="px-5 py-3 border-b border-cn-border bg-cn-bone-100/40 dark:bg-cn-ink-800/30">
                <span className="text-xs font-mono text-cn-text-muted">200 OK · application/json</span>
              </div>
              <pre
                className="text-[12.5px] leading-relaxed font-mono p-5 overflow-x-auto bg-cn-ink-900/95 text-cn-bone-100"
                data-testid="sandbox-response-block"
              >
{responsePreview}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DischargeSandboxTile;
